"use client"

import { create } from "zustand"
import { parseDiceRoll } from '@/lib/dice-roll'
import { toast } from "sonner"
import { createTranslator } from "next-intl"
import { LOCALE_COOKIE, LOCALES, DEFAULT_LOCALE, type Locale } from "@/i18n/locales"
import enMessages from "../messages/en.json"
import type { ChatSession, ChatMessage, MessageListResponse, MessageCreateData, CompanionChangelogEntry } from "./goonpals-types"

// A Zustand store action isn't a React component, so useTranslations()
// (a hook) can't be called here — next-intl's createTranslator() is the
// same catalog and lookup logic as a plain function instead, which a store
// action can call directly. Reads the same cookie i18n/request.ts uses;
// only fetches the locale's own JSON when it isn't English, matching that
// file's own lazy-import for non-default locales.
async function chatStoreT(key: string): Promise<string> {
  let locale: Locale = DEFAULT_LOCALE
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${LOCALE_COOKIE}=([^;]*)`))
    const stored = match ? decodeURIComponent(match[1]) : undefined
    if (stored && (LOCALES as readonly string[]).includes(stored)) locale = stored as Locale
  } catch {}
  let messages: any = enMessages
  if (locale !== DEFAULT_LOCALE) {
    try {
      const localeMessages = (await import(`../messages/${locale}.json`)).default as any
      messages = { ...enMessages, ...localeMessages, goonpalsChatStore: { ...(enMessages as any).goonpalsChatStore, ...localeMessages.goonpalsChatStore } }
    } catch { /* fall back to English below */ }
  }
  return createTranslator({ locale, messages, namespace: "goonpalsChatStore" })(key)
}

interface ChatStore {
  // State
  sessions: ChatSession[]
  activeSessionId: string | null
  messages: Record<string, ChatMessage[]> // sessionId -> messages
  loading: boolean
  /** sessionId -> true while the next bubble of a multi-bubble reply is being "typed" (shows the typing indicator). */
  followUpTyping: Record<string, boolean>
  error: string | null
  unlockRequired: { unlockPriceGoop: number } | null
  dailyLimitReached: { limit: number; used: number } | null
  /** Every configured text provider was rate-limited/unavailable at once —
   * a systemic, transient state (not the user's own daily cap), so it's
   * surfaced as a toast rather than a blocking modal. See
   * AllTextProvidersExhaustedError in lib/ai-model-router.ts. */
  allProvidersExhausted: boolean

  /** This companion's newest changelog entry, if this viewer hasn't seen it
   * yet — keyed by sessionId. Cleared (both here and server-side via
   * /changelog-seen) by dismissChangelogEntry. */
  pendingChangelogEntry: Record<string, CompanionChangelogEntry | null>

  /** The companion's own psychological state for this session (see
   * lib/psych-state.ts) — read-only, decayed client-side for display.
   * Keyed by sessionId. */
  companionPsychState: Record<string, { psychState: unknown; psychEventLog: unknown[] } | null>

  // Pagination
  hasMore: Record<string, boolean> // sessionId -> hasMore

  // Actions
  fetchSessions: (companionId?: string, includeArchived?: boolean) => Promise<ChatSession[]>

  fetchSessionById: (sessionId: string) => Promise<ChatSession | null>

  /** Marks the pending changelog entry as seen — clears local state and
   * tells the server so the card badge/banner don't reappear. */
  dismissChangelogEntry: (sessionId: string, companionId: string) => Promise<void>
  
  createSession: (companionId: string, forceNew?: boolean, plotId?: string | null) => Promise<ChatSession | null>

  updateSession: (sessionId: string, patch: { title?: string; archived?: boolean; experienceTone?: 'safe' | 'sfw' | 'flexible' | 'nsfw' | 'ensfw'; activeRoleVariantId?: string | null; bodyPreference?: 'eggplant' | 'grapefruit' | null; includeCommunityContent?: boolean; agencyPreference?: 'follow' | 'off'; limitsSoft?: boolean; diceHidden?: boolean; diceMode?: 'auto' | 'manual'; glympassEnabled?: boolean }) => Promise<ChatSession | null>
  
  setActiveSession: (sessionId: string | null) => void
  
  fetchMessages: (sessionId: string, before?: string, limit?: number) => Promise<void>
  
  sendMessage: (sessionId: string, data: MessageCreateData) => Promise<(ChatMessage & { combatState?: any; scriptTrigger?: any; contentSoftened?: boolean; activeSceneId?: string | null; arcReadyToConsolidate?: boolean }) | null>
  
  deleteSession: (sessionId: string) => Promise<boolean>

  setTtsDialogueOnly: (sessionId: string, dialogueOnly: boolean) => Promise<void>

  clearMessages: (sessionId: string) => void

  /** Appends a message arriving from a realtime subscription. Accepts either
   * the camelCase ChatMessage shape or the raw snake_case DB row shape
   * (postgres_changes payloads are always the latter). */
  addMessage: (sessionId: string, message: ChatMessage | Record<string, any>) => void

  /** Patches fields on an already-loaded message (e.g. a scene image that
   * just finished generating on demand) without a full refetch. */
  updateMessage: (sessionId: string, messageId: string, patch: Partial<ChatMessage>) => void

  reset: () => void
}

function normalizeSession(raw: ChatSession | Record<string, any>): ChatSession {
  if ("companionId" in raw) return raw as ChatSession
  return {
    id: raw.id,
    userId: raw.user_id,
    companionId: raw.companion_id,
    title: raw.title || "New Chat",
    archivedAt: raw.archived_at ? new Date(raw.archived_at) : null,
    currentMorale: raw.current_morale,
    messageCount: raw.message_count ?? 0,
    lastMessageAt: raw.last_message_at ? new Date(raw.last_message_at) : null,
    memoryEntries: raw.memory_entries ?? [],
    ttsDialogueOnly: raw.tts_dialogue_only ?? false,
    experienceTone: (['safe', 'sfw', 'flexible', 'nsfw', 'ensfw'] as const).includes(raw.experience_tone)
      ? raw.experience_tone
      : 'safe',
    plotId: raw.plot_id ?? null,
    plotTitle: raw.plot_title ?? null,
    plotPremise: raw.plot_premise ?? null,
    plotOpeningLine: raw.plot_opening_line ?? null,
    plotMediaUrl: raw.plot_media_url ?? null,
    plotMediaType: raw.plot_media_type === 'video' ? 'video' : raw.plot_media_type === 'image' ? 'image' : null,
    storyState: raw.story_state ?? null,
    activeRoleVariantId: raw.active_role_variant_id ?? null,
    bodyPreference: raw.body_preference ?? null,
    activeSceneId: raw.active_scene_id ?? null,
    includeCommunityContent: raw.include_community_content ?? true,
    agencyPreference: raw.agency_preference === 'off' ? 'off' : 'follow',
    limitsSoft: !!raw.limits_soft,
    diceHidden: !!raw.dice_hidden,
    diceMode: raw.dice_mode === 'manual' ? 'manual' : 'auto',
    glympassEnabled: raw.glympass_enabled ?? false,
    createdAt: raw.created_at ? new Date(raw.created_at) : new Date(),
    updatedAt: raw.updated_at ? new Date(raw.updated_at) : new Date(),
  }
}

function normalizeMessage(sessionId: string, raw: ChatMessage | Record<string, any>): ChatMessage {
  if ("senderType" in raw) {
    const message = raw as ChatMessage
    return {
      ...message,
      speakerEmotionKey: message.speakerEmotionKey ?? null,
      medium: message.medium ?? null,
      cutoutUrl: message.cutoutUrl ?? null,
      gamePayload: message.gamePayload ?? null,
      narrativeActions: message.narrativeActions ?? [],
      loreReveals: message.loreReveals ?? [],
      deliveredAssets: message.deliveredAssets ?? [],
      diceRoll: message.diceRoll ?? null,
      specialMediaUrl: message.specialMediaUrl ?? null,
      specialMediaKind: message.specialMediaKind ?? null,
    }
  }
  return {
    id: raw.id,
    sessionId: raw.session_id ?? sessionId,
    senderType: raw.sender_type,
    messageType: raw.message_type,
    content: raw.content,
    avatarUrl: raw.avatar_url ?? null,
    sceneUrl: raw.scene_url ?? null,
    sceneDisplayMode: raw.scene_display_mode === 'background' ? 'background' : 'inline',
    videoUrl: raw.video_url ?? null,
    audioUrl: raw.audio_url ?? null,
    wordTimings: Array.isArray(raw.word_timings) ? raw.word_timings : null,
    moraleDelta: raw.morale_delta ?? 0,
    speakerName: raw.speaker_name ?? null,
    speakerAvatarUrl: raw.speaker_avatar_url ?? null,
    speakerAvatarFocalX: raw.speaker_avatar_focal_x ?? null,
    speakerAvatarFocalY: raw.speaker_avatar_focal_y ?? null,
    speakerEmotionKey: raw.speaker_emotion_key ?? null,
    medium: raw.medium === 'text' || raw.medium === 'in_person' ? raw.medium : null,
    cutoutUrl: raw.cutout_url ?? null,
    gamePayload: raw.game_payload ?? null,
    narrativeActions: Array.isArray(raw.narrative_actions) ? raw.narrative_actions : [],
    loreReveals: Array.isArray(raw.lore_reveals) ? raw.lore_reveals : [],
    deliveredAssets: Array.isArray(raw.delivered_assets) ? raw.delivered_assets : [],
    diceRoll: parseDiceRoll(raw.dice_roll),
    specialMediaUrl: raw.special_media_url ?? null,
    specialMediaKind: raw.special_media_kind ?? null,
    tokensUsed: raw.tokens_used ?? null,
    generationTimeMs: raw.generation_time_ms ?? null,
    createdAt: raw.created_at ? new Date(raw.created_at) : new Date(),
  }
}

export const useChatStore = create<ChatStore>((set, get) => ({
  // Initial state
  sessions: [],
  activeSessionId: null,
  messages: {},
  loading: false,
  followUpTyping: {},
  error: null,
  unlockRequired: null,
  dailyLimitReached: null,
  allProvidersExhausted: false,
  pendingChangelogEntry: {},
  companionPsychState: {},
  hasMore: {},
  
  // Fetch all chat sessions
  fetchSessions: async (companionId?: string, includeArchived = false) => {
    set({ loading: true, error: null })
    
    try {
      const queryParams = new URLSearchParams()
      if (companionId) queryParams.append('companionId', companionId)
      if (includeArchived) queryParams.append('includeArchived', 'true')
      
      const response = await fetch(`/api/chat/sessions?${queryParams.toString()}`)
      
      if (!response.ok) {
        throw new Error(`Failed to fetch sessions: ${response.statusText}`)
      }
      
      const body = await response.json()
      const sessions = (body.sessions || []).map(normalizeSession)

      set({
        sessions,
        activeSessionId: sessions.some((session: ChatSession) => session.id === get().activeSessionId)
          ? get().activeSessionId
          : (sessions.find((session: ChatSession) => !session.archivedAt)?.id ?? null),
        loading: false,
      })
      return sessions
    } catch (error) {
      console.error("Error fetching sessions:", error)
      set({
        error: error instanceof Error ? error.message : "Failed to fetch sessions",
        loading: false,
      })
      return []
    }
  },
  
  // Fetch single session by ID
  fetchSessionById: async (sessionId: string) => {
    set({ loading: true, error: null })
    
    try {
      const response = await fetch(`/api/chat/sessions/${sessionId}`)
      
      if (!response.ok) {
        throw new Error(`Failed to fetch session: ${response.statusText}`)
      }
      
      const body = await response.json()
      const session = normalizeSession(body.session)

      set((state) => ({
        sessions: [session, ...state.sessions.filter((existing) => existing.id !== session.id)],
        pendingChangelogEntry: { ...state.pendingChangelogEntry, [session.id]: body.unseenChangelogEntry ?? null },
        companionPsychState: { ...state.companionPsychState, [session.id]: { psychState: body.psychState ?? null, psychEventLog: body.psychEventLog ?? [] } },
        loading: false,
      }))
      // Hardcore Realism catch-up (see lib/story-realism-sim.ts) — a
      // one-time notice of what happened in the world while this specific
      // session was closed. Only ever present when the story opted in.
      if (body.realismCatchUpSummary) {
        toast.info(await chatStoreT("whileYouWereAway"), { description: body.realismCatchUpSummary, duration: 10000 })
      }
      return session
    } catch (error) {
      console.error("Error fetching session:", error)
      set({
        error: error instanceof Error ? error.message : "Failed to fetch session",
        loading: false,
      })
      return null
    }
  },

  dismissChangelogEntry: async (sessionId: string, companionId: string) => {
    set((state) => ({ pendingChangelogEntry: { ...state.pendingChangelogEntry, [sessionId]: null } }))
    try {
      await fetch(`/api/companions/${companionId}/changelog-seen`, { method: "POST" })
    } catch (error) {
      console.error("Error dismissing changelog entry:", error)
    }
  },

  // Create a new chat session
  createSession: async (companionId: string, forceNew = false, plotId: string | null = null) => {
    set({ loading: true, error: null })
    
    try {
      const response = await fetch('/api/chat/sessions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ companionId, forceNew, plotId }),
      })
      
      if (!response.ok) {
        throw new Error(`Failed to create session: ${response.statusText}`)
      }
      
      const isExisting = response.status === 200
      const session = normalizeSession(await response.json())

      set((state) => ({
        sessions: [session, ...state.sessions.filter((existing) => existing.id !== session.id)],
        activeSessionId: session.id,
        messages: {
          ...state.messages,
          [session.id]: isExisting ? (state.messages[session.id] ?? []) : [],
        },
        loading: false,
      }))

      // The endpoint is idempotent — a 200 means this session already had
      // history, which the create response doesn't include. Fetch it now
      // instead of leaving the chat looking empty.
      if (isExisting) await get().fetchMessages(session.id)

      return session
    } catch (error) {
      console.error("Error creating session:", error)
      set({
        error: error instanceof Error ? error.message : "Failed to create session",
        loading: false,
      })
      return null
    }
  },

  updateSession: async (sessionId, patch) => {
    try {
      const response = await fetch(`/api/chat/sessions/${sessionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      })
      if (!response.ok) throw new Error('Failed to update conversation')
      const session = normalizeSession(await response.json())
      set((state) => ({
        sessions: patch.archived
          ? state.sessions.filter((item) => item.id !== sessionId)
          : state.sessions.map((item) => item.id === sessionId ? session : item),
        activeSessionId: patch.archived && state.activeSessionId === sessionId ? null : state.activeSessionId,
      }))
      return session
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update conversation' })
      return null
    }
  },
  
  // Set active session
  setActiveSession: (sessionId: string | null) => {
    set({ activeSessionId: sessionId })
  },
  
  // Fetch messages for a session
  fetchMessages: async (sessionId: string, before?: string, limit = 50) => {
    set({ loading: true, error: null })
    
    try {
      const queryParams = new URLSearchParams()
      if (before) queryParams.append('before', before)
      queryParams.append('limit', String(limit))
      
      const response = await fetch(`/api/chat/sessions/${sessionId}/messages?${queryParams.toString()}`)
      
      if (!response.ok) {
        throw new Error(`Failed to fetch messages: ${response.statusText}`)
      }
      
      const data: MessageListResponse = await response.json()
      const normalized = (data.messages || []).map((m: any) => normalizeMessage(sessionId, m))

      set((state) => ({
        messages: {
          ...state.messages,
          [sessionId]: before
            ? [...(state.messages[sessionId] || []), ...normalized]
            : normalized,
        },
        hasMore: {
          ...state.hasMore,
          [sessionId]: data.hasMore,
        },
        loading: false,
      }))
    } catch (error) {
      console.error("Error fetching messages:", error)
      set({
        error: error instanceof Error ? error.message : "Failed to fetch messages",
        loading: false,
      })
    }
  },
  
  // Send a message
  sendMessage: async (sessionId: string, data: MessageCreateData) => {
    set({ loading: true, error: null })

    // Show the user's own message immediately instead of waiting for the
    // full round trip (AI generation can take several seconds) — otherwise
    // it looks like the send did nothing until both messages pop in at once.
    // Swapped out below for the real row once the server responds.
    const optimisticId = data.silentNarrativeAction ? null : `optimistic-${Date.now()}`
    if (optimisticId) {
      const optimisticMessage: ChatMessage = {
        id: optimisticId,
        sessionId,
        senderType: 'user',
        messageType: data.messageType || 'text',
        content: data.content,
        avatarUrl: null,
        sceneUrl: null,
        sceneDisplayMode: 'inline',
        videoUrl: null,
        audioUrl: null,
        moraleDelta: 0,
        speakerName: null,
        speakerAvatarUrl: null,
        speakerAvatarFocalX: null,
        speakerAvatarFocalY: null,
        speakerEmotionKey: null,
        cutoutUrl: null,
        gamePayload: null,
        narrativeActions: [],
        tokensUsed: null,
        generationTimeMs: null,
        createdAt: new Date(),
      }
      set((state) => ({
        messages: {
          ...state.messages,
          [sessionId]: [...(state.messages[sessionId] || []), optimisticMessage],
        },
      }))
    }

    try {
      const response = await fetch(`/api/chat/sessions/${sessionId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })
      
      if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        if (body.requiresUnlock) {
          const err: any = new Error(body.error || 'This companion requires unlocking')
          err.requiresUnlock = true
          err.unlockPriceGoop = body.unlockPriceGoop
          throw err
        }
        if (body.dailyLimitReached) {
          const err: any = new Error(body.error || 'Daily chat limit reached')
          err.dailyLimitReached = true
          err.limit = body.limit
          err.used = body.used
          throw err
        }
        if (body.allProvidersExhausted) {
          const err: any = new Error(body.error || 'Every AI provider is tapped out right now')
          err.allProvidersExhausted = true
          throw err
        }
        throw new Error(body.error || `Failed to send message: ${response.statusText}`)
      }

      const body = await response.json()
      const userMessage = body.userMessage ? normalizeMessage(sessionId, body.userMessage) : null
      const aiMessage = normalizeMessage(sessionId, body.aiMessage)
      const followUpMessages: ChatMessage[] = (body.followUpMessages || []).map(
        (m: any) => normalizeMessage(sessionId, m)
      )
      const goonDeckUnlockMessages: ChatMessage[] = (body.goonDeckUnlockMessages || []).map(
        (m: any) => normalizeMessage(sessionId, m)
      )
      const crossTalkMessages: ChatMessage[] = (body.crossTalkMessages || []).map(
        (m: any) => normalizeMessage(sessionId, m)
      )

      // Swap the optimistic placeholder for the real user message, then add
      // the AI/reward messages after it. Follow-up messages (the "double
      // text" humanization split) are staggered in afterward below instead
      // of appearing in this same batch — real people don't send a second
      // text in the same instant as the first.
      set((state) => {
        const existing = optimisticId
          ? (state.messages[sessionId] || []).filter((m) => m.id !== optimisticId)
          : (state.messages[sessionId] || [])
        const existingIds = new Set(existing.map((m) => m.id))
        const newOnes = [userMessage, aiMessage, ...goonDeckUnlockMessages].filter((m): m is ChatMessage => Boolean(m)).filter(
          (m) => !existingIds.has(m.id)
        )
        return {
          messages: {
            ...state.messages,
            [sessionId]: [...existing, ...newOnes],
          },
          sessions: body.storyState
            ? state.sessions.map((session) => session.id === sessionId ? { ...session, storyState: body.storyState } : session)
            : state.sessions,
          loading: false,
        }
      })

      // Fire-and-forget — deliberately not awaited, so sendMessage's own
      // promise resolves right away and this trickles in on its own, timed
      // from the moment the primary message actually lands in state (not
      // from when the request started) — 3-4s apart per follow-up, matching
      // how long it takes a real person to send a second text.
      ;(async () => {
        const setTyping = (typing: boolean) =>
          set((state) => ({ followUpTyping: { ...state.followUpTyping, [sessionId]: typing } }))
        try {
          for (let i = 0; i < followUpMessages.length; i++) {
            const followUp = followUpMessages[i]
            // Texting-style bubbles (one text per line) arrive like someone typing them: the wait is the time it takes
            // to type that line (a short one is quick, a long one takes a while) plus a little human hesitation, and the
            // typing indicator shows in between. Other follow-ups keep the slower "double text" pace.
            const gapMs = followUp.medium === 'text'
              ? 600 + Math.min((followUp.content?.length ?? 0) * 55, 2600) + Math.random() * 600
              : 3000 + Math.random() * 1000
            setTyping(true)
            await new Promise((resolve) => setTimeout(resolve, gapMs))
            set((state) => ({
              messages: {
                ...state.messages,
                [sessionId]: [...(state.messages[sessionId] || []), followUp],
              },
              // Stay "typing" until the last bubble has landed.
              followUpTyping: { ...state.followUpTyping, [sessionId]: i < followUpMessages.length - 1 },
            }))
          }
        } finally {
          setTyping(false)
        }
      })()

      // Same staggered-reveal pattern as follow-ups above, but a different
      // speaker replying reads as "typing" pace rather than "double text"
      // pace — shorter, tighter gap.
      ;(async () => {
        for (const crossTalk of crossTalkMessages) {
          await new Promise((resolve) => setTimeout(resolve, 1500 + Math.random() * 1000))
          set((state) => ({
            messages: {
              ...state.messages,
              [sessionId]: [...(state.messages[sessionId] || []), crossTalk],
            },
          }))
        }
      })()

      // Update session metadata
      set((state) => ({
        sessions: state.sessions.map(s =>
          s.id === sessionId
            ? {
                ...s,
                title: s.title === "New Chat" ? data.content.trim().slice(0, 60) : s.title,
                messageCount: s.messageCount + (userMessage ? 2 : 1) + followUpMessages.length + goonDeckUnlockMessages.length + crossTalkMessages.length,
                lastMessageAt: new Date(),
              }
            : s
        ),
      }))

      return { ...aiMessage, combatState: body.combatState, survivalStatus: body.survivalStatus, currencyUpdate: body.currencyUpdate, scriptTrigger: body.scriptTrigger, contentSoftened: !!body.contentSoftened, activeSceneId: body.activeSceneId ?? null, arcReadyToConsolidate: !!body.arcReadyToConsolidate }
    } catch (error: any) {
      console.error("Error sending message:", error)
      set((state) => ({
        messages: {
          ...state.messages,
          [sessionId]: (state.messages[sessionId] || []).filter((m) => m.id !== optimisticId),
        },
        error: error instanceof Error ? error.message : "Failed to send message",
        unlockRequired: error?.requiresUnlock ? { unlockPriceGoop: error.unlockPriceGoop } : null,
        dailyLimitReached: error?.dailyLimitReached ? { limit: error.limit, used: error.used } : null,
        allProvidersExhausted: !!error?.allProvidersExhausted,
        loading: false,
      }))
      return null
    }
  },

  // Delete a session
  deleteSession: async (sessionId: string) => {
    set({ loading: true, error: null })
    
    try {
      const response = await fetch(`/api/chat/sessions/${sessionId}`, {
        method: 'DELETE',
      })
      
      if (!response.ok) {
        throw new Error(`Failed to delete session: ${response.statusText}`)
      }
      
      set((state) => {
        const newMessages = { ...state.messages }
        delete newMessages[sessionId]
        
        const newHasMore = { ...state.hasMore }
        delete newHasMore[sessionId]
        
        return {
          sessions: state.sessions.filter(s => s.id !== sessionId),
          messages: newMessages,
          hasMore: newHasMore,
          activeSessionId: state.activeSessionId === sessionId ? null : state.activeSessionId,
          loading: false,
        }
      })
      
      return true
    } catch (error) {
      console.error("Error deleting session:", error)
      set({
        error: error instanceof Error ? error.message : "Failed to delete session",
        loading: false,
      })
      return false
    }
  },
  
  // Toggle whether voice generation for this chat reads only dialogue
  setTtsDialogueOnly: async (sessionId: string, dialogueOnly: boolean) => {
    // Optimistic — this is a low-stakes preference toggle, not worth a
    // loading spinner or rolling back on failure.
    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.id === sessionId ? { ...s, ttsDialogueOnly: dialogueOnly } : s
      ),
    }))
    try {
      await fetch(`/api/chat/sessions/${sessionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ttsDialogueOnly: dialogueOnly }),
      })
    } catch (error) {
      console.error('Error updating tts_dialogue_only:', error)
    }
  },

  // Clear messages for a session (local only)
  clearMessages: (sessionId: string) => {
    set((state) => {
      const newMessages = { ...state.messages }
      delete newMessages[sessionId]

      return {
        messages: newMessages,
      }
    })
  },

  // Append a message from a realtime subscription — skips it if already
  // present (the sender's own optimistic insert from sendMessage already
  // added it, so the realtime echo would otherwise duplicate it).
  addMessage: (sessionId: string, message: ChatMessage | Record<string, any>) => {
    const normalized = normalizeMessage(sessionId, message)
    set((state) => {
      const existing = state.messages[sessionId] || []
      if (existing.some((m) => m.id === normalized.id)) return state
      return {
        messages: {
          ...state.messages,
          [sessionId]: [...existing, normalized],
        },
      }
    })
  },

  updateMessage: (sessionId: string, messageId: string, patch: Partial<ChatMessage>) => {
    set((state) => {
      const existing = state.messages[sessionId] || []
      return {
        messages: {
          ...state.messages,
          [sessionId]: existing.map((m) => (m.id === messageId ? { ...m, ...patch } : m)),
        },
      }
    })
  },

  // Reset store
  reset: () => {
    set({
      sessions: [],
      activeSessionId: null,
      messages: {},
      loading: false,
      error: null,
      hasMore: {},
      pendingChangelogEntry: {},
      companionPsychState: {},
    })
  },
}))
